// file: src/router/index.js

import {createWebHistory, createRouter} from "vue-router";
import routeGuard from "@/router/middlewares/route-guard.middleware";
import routingConstants from "./routingConstants";

// Dynamic component imports for better code splitting
const Dashboard = () => import('@/components/UserDashboard/UserDashboard.vue');
const Logout = () => import('@/components/AdminDashboard/Pages/Logout.vue');
const Login = () => import('@/components/AdminDashboard/Pages/Login.vue');
const OnboardingForm = () => import('@/components/OnboardingForm.vue');
const DeveloperDashboard = () => import('@/components/UserDashboard/DeveloperDashboard.vue');
const UserTransactionsView = () => import('@/components/UserTransactions/UserTransactionsView.vue');

// Admin components with dynamic imports
const AdminLayout = () => import('@/components/AdminDashboard/AdminLayout.vue');
const AdminOverview = () => import('@/components/AdminDashboard/Pages/AdminOverview.vue');
const AdminConversations = () => import('@/components/AdminDashboard/Pages/AdminConversations.vue');
const AdminMessages = () => import('@/components/AdminDashboard/Pages/AdminMessages.vue');
const AdminSystemEvents = () => import('@/components/AdminDashboard/Pages/AdminSystemEvents.vue');
const AdminOnboardingSubmissions = () => import('@/components/AdminDashboard/Pages/AdminOnboardingSubmissions.vue');
const AdminCommunities = () => import('@/components/AdminDashboard/EnergyCommunity/AdminCommunities.vue');
const AdminCommunityDetail = () => import('@/components/AdminDashboard/EnergyCommunity/AdminCommunityDetail.vue');
const AdminFlexcoDetailWrapper = () => import('@/components/AdminDashboard/EnergyCommunity/AdminFlexcoDetailWrapper.vue');
const AdminUsers = () => import('@/components/AdminDashboard/Pages/AdminUsers.vue');
const AdminPremises = () => import('@/components/AdminDashboard/Pages/AdminPremises.vue');
const AdminMeteringPoints = () => import('@/components/AdminDashboard/Pages/AdminMeteringPoints.vue');
const AdminMeteringPointDetail = () => import('@/components/AdminDashboard/Pages/AdminMeteringPointDetail.vue');
const AdminDevtools = () => import('@/components/AdminDashboard/Pages/AdminDevtools.vue');
const AdminUserDetailWrapper = () => import('@/components/AdminDashboard/EnergyCommunity/Wrappers/AdminUserDetailWrapper.vue');
const CommunityUserDetailWrapper = () => import('@/components/AdminDashboard/EnergyCommunity/Wrappers/CommunityUserDetailWrapper.vue');
const AdminConversationDetailWrapper = () => import('@/components/AdminDashboard/EnergyCommunity/Wrappers/AdminConversationDetailWrapper.vue');
const CommunityConversationDetailWrapper = () => import('@/components/AdminDashboard/EnergyCommunity/Wrappers/CommunityConversationDetailWrapper.vue');
const AdminUserRecommendation = () => import('@/components/AdminDashboard/Pages/AdminUserRecommendation.vue');
const AdminOrganizations = () => import('@/components/AdminDashboard/Pages/AdminOrganizations.vue');
const AdminOrganizationDetail = () => import('@/components/AdminDashboard/Pages/AdminOrganizationDetail.vue');
const AdminEnergyCommunityAdmins = () => import('@/components/AdminDashboard/Pages/AdminEnergyCommunityAdmins.vue');
const AdminAccounts = () => import('@/components/AdminDashboard/Pages/AdminAccounts.vue');
const AdminFiles = () => import('@/components/AdminDashboard/Pages/AdminFiles.vue');
const AdminWhitelabels = () => import('@/components/AdminDashboard/Pages/AdminWhitelabels.vue');
const AdminWhitelabelDetail = () => import('@/components/AdminDashboard/Pages/AdminWhitelabelDetail.vue');
const AdminDocumentDesignDetail = () => import('@/components/AdminDashboard/Pages/AdminDocumentDesignDetail.vue');
const AdminBilling = () => import('@/components/AdminDashboard/Pages/AdminBilling.vue');

// Public onboarding components
const PublicOnboarding = () => import('@/components/Onboarding/PublicOnboarding.vue');
const PublicOnboardingThankYou = () => import('@/components/Onboarding/PublicOnboardingThankYou.vue');
const PublicOnboardingConfirm = () => import('@/components/Onboarding/PublicOnboardingConfirm.vue');

const routes = [
    {
        path: routingConstants.publicOnboarding,
        name: "PublicOnboarding",
        component: PublicOnboarding,
        props: true,
        meta: {
            requiresOnboarding: false,
            allowAnonymous: true
        }
    },
    {
        path: routingConstants.publicOnboardingThankYou,
        name: "PublicOnboardingThankYou",
        component: PublicOnboardingThankYou,
        meta: {
            requiresOnboarding: false,
            allowAnonymous: true
        }
    },
    {
        path: routingConstants.publicOnboardingConfirm,
        name: "PublicOnboardingConfirm",
        component: PublicOnboardingConfirm,
        props: true,
        meta: {
            requiresOnboarding: false,
            allowAnonymous: true
        }
    },
    {
        path: routingConstants.dashboardRoute,
        name: "Dashboard",
        component: Dashboard,
        meta: {
            requiresOnboarding: true
        }
    },
    {
        path: routingConstants.onboardingRoute,
        name: "Onboarding",
        component: OnboardingForm,
        meta: {
            requiresNotOnboarded: true
        }
    },
    {
        path: routingConstants.logoutRoute,
        name: "Logout",
        component: Logout
    },
    {
        path: routingConstants.loginRoute,
        name: "Login",
        component: Login,
        meta: {
            allowAnonymous: true
        }
    },
    {
        path: routingConstants.developerRoute,
        name: "Developer",
        component: DeveloperDashboard,
        meta: {
            requiresSuperAdmin: true
        }
    },
    {
        path: routingConstants.adminRoute,
        component: AdminLayout,
        meta: {
            requiresSuperAdmin: true
        },
        children: [
            {
                path: '',
                name: "AdminOverview",
                component: AdminOverview
            },
            {
                path: 'conversations',
                name: "AdminConversations",
                component: AdminConversations,
                children:[
                    {
                        path: ':conversationId',
                        name: "AdminConversationDetail",
                        component: AdminConversationDetailWrapper
                    }
                ]
            },
            {
                path: 'messages',
                name: "AdminMessages",
                component: AdminMessages
            },
            {
                path: "/admin/system-events",
                name: "AdminSystemEvents",
                component: AdminSystemEvents
            },
            {
                path: "/admin/onboarding-submissions",
                name: "AdminOnboardingSubmissions",
                component: AdminOnboardingSubmissions
            },
            {
                path: 'communities',
                name: "AdminCommunities",
                component: AdminCommunities,
                children: [
                    {
                        path: ':id',  // No leading slash
                        name: "AdminCommunityDetail",
                        component: AdminCommunityDetail,
                        props: true,
                        children: [
                            {
                                path: 'conversations/:conversationId',
                                name: "AdminCommunityConversationDetail",
                                component: CommunityConversationDetailWrapper,
                                props: true,
                            },
                            {
                                path: 'users/:userId',
                                name: "AdminUserDetail",
                                component: CommunityUserDetailWrapper,
                                props: true,
                            }
                        ]
                    }
                ]
            },
            {
                path: 'users',
                name: "AdminUsers",
                component: AdminUsers,
                children: [
                    {
                        path: ':userId',
                        name: "AdminUserDetailDirect",
                        component: AdminUserDetailWrapper
                    }]
            },
            {
                path: 'premises',
                name: "AdminPremises",
                component: AdminPremises
            },
            {
                path: 'metering-points',
                name: "AdminMeteringPoints",
                component: AdminMeteringPoints,
                children: [
                    {
                        path: ':meteringPointNumber',
                        name: "AdminMeteringPointDetail",
                        component: AdminMeteringPointDetail,
                        props: true
                    }
                ]
            },
            {
                path: 'billing',
                name: "AdminBilling",
                component: AdminBilling
            },
            {
                path: 'flexco',
                name: "AdminFlexco",
                component: AdminFlexcoDetailWrapper
            },
            {
                path: 'devtools',
                name: "AdminDevtools",
                component: AdminDevtools
            },
            {
                path: 'communities/:energyCommunityId/recommendations',
                name: "AdminCommunityRecommendations",
                component: AdminUserRecommendation,
                props: true
            },
            {
                path: 'organizations',
                name: "AdminOrganizations",
                component: AdminOrganizations,
                children: [
                    {
                        path: ':id',
                        name: "AdminOrganizationDetail",
                        component: AdminOrganizationDetail,
                        props: true
                    }
                ]
            },
            {
                path: 'ec-admins',
                name: "AdminEnergyCommunityAdmins",
                component: AdminEnergyCommunityAdmins
            },
            {
                path: 'accounts',
                name: "AdminAccounts",
                component: AdminAccounts
            },
            {
                path: 'files',
                name: "AdminFiles",
                component: AdminFiles
            },
            {
                path: 'whitelabels',
                name: "AdminWhitelabels",
                component: AdminWhitelabels,
                children: [
                    {
                        path: ':id',
                        name: "AdminWhitelabelDetail",
                        component: AdminWhitelabelDetail,
                        props: true
                    },
                    {
                        path: 'designs/:id',
                        name: "AdminDocumentDesignDetail",
                        component: AdminDocumentDesignDetail,
                        props: true
                    }
                ]
            },
            {
                path: 'user-transactions',
                name: "AdminUserTransactions",
                component: UserTransactionsView,
                meta: {
                    requiresDeveloper: true
                }
            }
        ]
    }
];

//https://github.com/keycloak/keycloak/issues/14742
const initializeRouter = () => {
    let routerOptions = {
        history: createWebHistory(),
        routes, // short for `routes: routes`
    };
    let router = createRouter(routerOptions);

    router.beforeEach(routeGuard);

    return router;
}

export { initializeRouter };